import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, Users, Eye, ExternalLink } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const RANGES = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

function formatShortDate(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export default function Analytics() {
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/analytics/overview", { params: { days: range } })
      .then(({ data }) => { if (!cancel) setData(data); })
      .catch((e) => { if (!cancel) toast.error(formatApiError(e)); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range]);

  if (loading || !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C8102E]" /></div>;
  }

  const chartData = data.daily.map((d) => ({ ...d, label: formatShortDate(d.date) }));

  return (
    <div data-testid="analytics-view" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-black text-2xl">Traffic</h2>
          <p className="text-sm text-zinc-600 font-medium">Only public/student traffic is counted — staff sessions are ignored.</p>
        </div>
        <div className="flex gap-1 border-2 border-black rounded-lg overflow-hidden bg-white">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-3 py-1.5 text-sm font-bold ${range === r.value ? "bg-[#C8102E] text-white" : "hover:bg-zinc-100"}`}
              data-testid={`analytics-range-${r.value}`}
            >
              Last {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={<Eye className="h-5 w-5" />} label="Views (7d)" value={data.totals["7d"].views} bg="#FEF3C7" testId="stat-views-7" />
        <StatCard icon={<Users className="h-5 w-5" />} label="Visitors (7d)" value={data.totals["7d"].visitors} bg="#BBF7D0" testId="stat-visitors-7" />
        <StatCard icon={<Eye className="h-5 w-5" />} label={`Views (${range}d)`} value={chartData.reduce((s, d) => s + d.views, 0)} bg="#FDE68A" testId="stat-views-range" />
        <StatCard icon={<TrendingUp className="h-5 w-5" />} label={`Peak day (${range}d)`} value={Math.max(0, ...chartData.map((d) => d.views))} bg="#FECACA" testId="stat-peak" />
      </div>

      {/* Line chart */}
      <div className="brut-card-static p-5">
        <div className="font-display font-bold text-lg mb-3">Daily traffic</div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C8102E" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#C8102E" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fontWeight: 600 }} stroke="#0A0A0A" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fontWeight: 600 }} stroke="#0A0A0A" />
              <Tooltip
                contentStyle={{ border: "2px solid #000", borderRadius: 8, boxShadow: "4px 4px 0 0 #000", fontFamily: "Work Sans", fontWeight: 600 }}
                labelStyle={{ fontWeight: 800, marginBottom: 4 }}
              />
              <Area type="monotone" dataKey="views" name="Views" stroke="#C8102E" strokeWidth={2.5} fill="url(#viewsFill)" />
              <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#0A0A0A" strokeWidth={1.5} strokeDasharray="4 4" fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top items */}
      <div className="grid lg:grid-cols-3 gap-5">
        <TopList
          title="Top opportunities"
          rows={data.top_opportunities}
          renderRow={(r) => (
            <Link to={`/opportunity/${r.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 hover:underline">
              <span className="truncate text-sm font-bold">{r.title}</span>
              <span className="text-xs font-black text-[#C8102E]">{r.views}</span>
            </Link>
          )}
          empty="No opportunity views yet."
          testId="top-opps"
        />
        <TopList
          title="Top blog posts"
          rows={data.top_posts}
          renderRow={(r) => (
            <Link to={`/blog/${r.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 hover:underline">
              <span className="truncate text-sm font-bold">{r.title}</span>
              <span className="text-xs font-black text-[#C8102E]">{r.views}</span>
            </Link>
          )}
          empty="No blog views yet."
          testId="top-posts"
        />
        <TopList
          title="Top pages"
          rows={data.top_paths}
          renderRow={(r) => (
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-mono">{r.path}</span>
              <span className="text-xs font-black text-[#C8102E]">{r.views}</span>
            </div>
          )}
          empty="No traffic in this range yet."
          testId="top-paths"
        />
      </div>

      <p className="text-xs text-zinc-500 flex items-center gap-1.5">
        <ExternalLink className="h-3 w-3" />
        Visitors approximated by hashing IP + user-agent per day. No cookies, no personal data stored.
      </p>
    </div>
  );
}

function StatCard({ icon, label, value, bg, testId }) {
  return (
    <div className="brut-card-static p-5" style={{ backgroundColor: bg }} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-bold text-black/70 mb-1">{icon}{label}</div>
      <div className="font-display font-black text-3xl">{value}</div>
    </div>
  );
}

function TopList({ title, rows, renderRow, empty, testId }) {
  return (
    <div className="brut-card-static p-5" data-testid={testId}>
      <div className="font-display font-bold text-lg mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-600 font-medium">{empty}</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="border-2 border-black rounded-lg px-3 py-2 bg-white">{renderRow(r)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
